import React, {useEffect, useRef, useState} from "react";
import {Field} from "react-final-form";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import Toast from "cogo-toast";
import {Link} from "react-router-dom";
import {FiChevronDown, FiChevronUp, FiPlus, FiRefreshCcw, FiSearch, FiSettings, FiUpload, FiX} from "react-icons/fi";
import {TurnstileButton} from "../components/Button";
import PageHeader from "../components/PageHeader";
import InputField from "../../../FinalForm/InputField";
import ApiTariffSelectField from "../../../FinalForm/api/ApiTariffSelectField";
import {DefaultLoader} from "../../../components/DefaultLoader";
import {apiPostFilterTicket} from "./Actions";
import TicketTable from "./TicketTable";
import TicketFormModal from "./TicketFormModal";
import {useIsMobile} from "../components/Helper";
import Filter from "../components/Filter";

// ─── main ─────────────────────────────────────────────────────────────────────

const chipBase   = {borderRadius: "999px", padding: "5px 12px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, cursor: "pointer", border: "1px solid #cbd5e1", display: "inline-flex", alignItems: "center", gap: "4px"};
const chipActive = {...chipBase, background: "#2563eb", color: "#fff", border: "1px solid #2563eb", fontWeight: 600};

const Ticket = () => {
    const [data, setData]               = useState(null);
    const [loading, setLoading]         = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [ticketModal, setTicketModal] = useState(false);
    const [page, setPage]               = useState(0);
    const [pageSize, setPageSize]       = useState(100);
    const [total, setTotal]             = useState(0);
    const [serverMode, setServerMode]   = useState(false);
    const isMobile                      = useIsMobile();
    const formRef                       = useRef(null);

    const user = useSelector(s => s.user);
    const {filterTicket} = useSelector(s => ({filterTicket: s.filterTicket}), shallowEqual);
    const dispatch = useDispatch();

    const filterData = filterTicket.filterTicketData;

    const advancedCount =
        ((filterData?.tariff ? 1 : 0))
        + (filterData?.name  ? 1 : 0)
        + (filterData?.note  ? 1 : 0)
        + (filterData?.hasImage !== undefined && filterData?.hasImage !== null ? 1 : 0);

    const getData = (filterValues, pageArg = 0, sizeArg = pageSize) => {
        setLoading(true);
        dispatch({type: "SET_FILTER_TICKET", payload: filterValues});
        const payload = {...filterValues, offset: pageArg * sizeArg, limit: sizeArg};
        apiPostFilterTicket(user.activeAreal.id, payload, (d) => {
            if (Array.isArray(d)) {
                // starý BE bez stránkování → client-side ETable
                setServerMode(false);
                setData(d);
                setTotal(d.length);
            } else {
                // nový BE → server-side stránkování
                setServerMode(true);
                setData(d?.items ?? []);
                setTotal(d?.total ?? 0);
            }
            setPage(pageArg);
            setPageSize(sizeArg);
            setLoading(false);
        }, () => {
            Toast.error("Při načítání vstupenek nastala chyba.");
            setData(null);
            setLoading(false);
        });
    };

    const handlePage = (p) => getData(filterData, p, pageSize);
    const handlePageSize = (s) => getData(filterData, 0, s);

    return (
        <>
            {user.activeAreal && (
                <>
                    <PageHeader crumbs={[{label: "Vstupenky"}]}>
                        <TurnstileButton link="/ticket/add" text="PŘIDAT VSTUPENKY" icon={<FiPlus size={12}/>}/>
                        {user?.user?.rights?.enigooTurniketsEdit && (
                            <TurnstileButton
                                onClick={() => setTicketModal(true)}
                                text="IMPORT VSTUPENEK"
                                icon={<FiUpload size={12}/>}
                                color="outline"
                            />
                        )}
                    </PageHeader>

                    {/* filter */}
                    <Filter
                        onSubmit={(values) => getData(values, 0, pageSize)}
                        initialValues={filterData}
                        formRef={formRef}
                        loading={loading}
                    >
                        {({values, form, submit}) => {
                            const hasAnyFilter = !!(values?.bc || values?.name || values?.note || values?.tariff
                                || values?.hasImage !== undefined && values?.hasImage !== null);
                            return (
                                <>
                                    {/* BC search */}
                                    <div style={{position: "relative", marginBottom: "8px"}}>
                                        <FiSearch size={15} style={{position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none"}}/>
                                        <Field name="bc">
                                            {({input}) => (
                                                <input
                                                    {...input}
                                                    type="text"
                                                    placeholder="Hledat podle BC..."
                                                    onKeyDown={e => e.key === "Enter" && submit()}
                                                    style={{width: "100%", padding: "9px 14px 9px 38px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "var(--enigoo-fs-md)", background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box"}}
                                                />
                                            )}
                                        </Field>
                                    </div>

                                    {/* chips row */}
                                    <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", marginBottom: "8px"}}>
                                        <button type="button"
                                            onClick={() => setAdvancedOpen(v => !v)}
                                            style={{...chipBase, color: advancedCount > 0 ? "#2563eb" : "#475569", border: `1px solid ${advancedCount > 0 ? "#bfdbfe" : "#cbd5e1"}`, background: advancedCount > 0 ? "#eff6ff" : "#fff"}}
                                        >
                                            <FiSettings size={11}/>
                                            Pokročilé
                                            {advancedCount > 0 && (
                                                <span style={{background: "#2563eb", color: "#fff", fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, borderRadius: "999px", padding: "1px 6px"}}>
                                                    {advancedCount}
                                                </span>
                                            )}
                                            {advancedOpen ? <FiChevronUp size={11}/> : <FiChevronDown size={11}/>}
                                        </button>

                                        {hasAnyFilter && (
                                            <button type="button"
                                                onClick={() => { dispatch({type: "FILTER_TICKET_RESET"}); form.reset({}); }}
                                                style={{marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "var(--enigoo-fs-xs)", display: "flex", alignItems: "center", gap: "3px"}}
                                            >
                                                <FiX size={11}/> Zrušit filtry
                                            </button>
                                        )}
                                    </div>

                                    {/* advanced */}
                                    {advancedOpen && (
                                        <div style={{padding: "12px", marginBottom: "8px"}}>
                                            <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px"}}>
                                                <ApiTariffSelectField name={"tariff"} isMulti={false} disabled={false} onEnter={submit}/>
                                                <InputField label={"Jméno"} name={"name"} isMandatory={false} onEnter={submit}/>
                                                <InputField label={"Poznámka"} name={"note"} isMandatory={false} onEnter={submit}/>
                                                <div className={"flex flex-col text-sm p-1"}>
                                                    <div className="form-label font-bold flex flex-col pt-1 pb-1"><span>Fotka:</span></div>
                                                    <div style={{display: "flex", flexWrap: "nowrap", gap: "6px"}}>
                                                        {[{v: null, l: "Vše"}, {v: true, l: "S fotkou"}, {v: false, l: "Bez fotky"}].map(opt => {
                                                            const cur = values?.hasImage;
                                                            const active = opt.v === null ? (cur === null || cur === undefined) : cur === opt.v;
                                                            return (
                                                                <button
                                                                    key={String(opt.v)}
                                                                    type="button"
                                                                    onClick={() => form.change("hasImage", opt.v === null ? undefined : opt.v)}
                                                                    style={active
                                                                        ? {background: "#2563eb", color: "#fff", border: "1px solid #2563eb", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap"}
                                                                        : {background: "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap"}
                                                                    }
                                                                >
                                                                    {opt.l}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        }}
                    </Filter>

                    {/* body */}
                    {loading
                        ? <DefaultLoader text={"Načítám vstupenky..."}/>
                        : data != null
                            ? <TicketTable
                                data={data}
                                setData={setData}
                                serverMode={serverMode}
                                page={page}
                                pageSize={pageSize}
                                total={total}
                                onPage={handlePage}
                                onPageSize={handlePageSize}
                            />
                            : null
                    }

                    <TicketFormModal
                        visible={ticketModal}
                        close={(renew) => {
                            setTicketModal(false);
                            if (renew && filterData) getData(filterData);
                        }}
                    />
                </>
            )}
        </>
    );
};

export default Ticket;
